import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const BulkActionsModal = ({ isOpen, onClose, selectedClients, clients, onClearSelection }) => {
  const [selectedAction, setSelectedAction] = useState('');
  const [emailTemplate, setEmailTemplate] = useState('');
  const [smsMessage, setSmsMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen) return null;

  const selectedClientData = clients.filter(client => selectedClients.includes(client.id));

  const emailTemplates = [
    {
      id: 'case_update',
      name: 'Case Update',
      subject: 'Update on Your Legal Matter',
      content: `Dear [CLIENT_NAME],

I hope this email finds you well. I wanted to provide you with an update on your legal matter.

[UPDATE_DETAILS]

Please don't hesitate to contact me if you have any questions or concerns.

Best regards,
[LAWYER_NAME]
LawDesk KE`
    },
    {
      id: 'appointment_reminder',name: 'Appointment Reminder',subject: 'Appointment Reminder - [DATE]',
      content: `Dear [CLIENT_NAME],

This is a friendly reminder about your upcoming appointment scheduled for [DATE] at [TIME].

Please bring any relevant documents we discussed.

If you need to reschedule, please contact our office as soon as possible.

Best regards,
[LAWYER_NAME]
LawDesk KE`
    },
    {
      id: 'document_request',name: 'Document Request',subject: 'Required Documents for Your Case',
      content: `Dear [CLIENT_NAME],

To proceed with your case, we require the following documents:

[DOCUMENT_LIST]

Please provide these documents at your earliest convenience.

Thank you for your cooperation.

Best regards,
[LAWYER_NAME]
LawDesk KE`
    }
  ];

  const smsTemplates = [
    {
      id: 'appointment_reminder',
      name: 'Appointment Reminder',
      content: 'Reminder: You have an appointment with LawDesk KE on [DATE] at [TIME]. Please confirm or call to reschedule.'
    },
    {
      id: 'payment_reminder',
      name: 'Payment Reminder',
      content: 'Dear [CLIENT_NAME], this is a reminder that your invoice [INVOICE_NUMBER] is due. Please make payment via M-Pesa or contact our office.'
    },
    {
      id: 'case_update',
      name: 'Case Update',
      content: 'Update on your case: [UPDATE]. For more details, please check your client portal or contact our office.'
    }
  ];

  const handleActionChange = (action) => {
    setSelectedAction(action);
    if (action === 'send_email') {
      setEmailTemplate(emailTemplates[0].content);
    } else if (action === 'send_sms') {
      setSmsMessage(smsTemplates[0].content);
    }
  };

  const handleTemplateSelect = (template, type) => {
    if (type === 'email') {
      setEmailTemplate(template.content);
    } else if (type === 'sms') {
      setSmsMessage(template.content);
    }
  };

  const handleExecuteAction = async () => {
    setIsProcessing(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    console.log('Executing action:', selectedAction, 'for clients:', selectedClients);
    
    setIsProcessing(false);
    onClearSelection();
    onClose();
  };

  const renderActionContent = () => {
    switch (selectedAction) {
      case 'send_email':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Email Template
              </label>
              <select
                onChange={(e) => {
                  const template = emailTemplates.find(t => t.id === e.target.value);
                  if (template) handleTemplateSelect(template, 'email');
                }}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Select a template</option>
                {emailTemplates.map(template => (
                  <option key={template.id} value={template.id}>{template.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Email Content
              </label>
              <textarea
                value={emailTemplate}
                onChange={(e) => setEmailTemplate(e.target.value)}
                rows={8}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Enter email content..."
              />
              <p className="text-xs text-text-secondary mt-1">
                Use [CLIENT_NAME], [DATE], [TIME] as placeholders
              </p>
            </div>
          </div>
        );

      case 'send_sms':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                SMS Template
              </label>
              <select
                onChange={(e) => {
                  const template = smsTemplates.find(t => t.id === e.target.value);
                  if (template) handleTemplateSelect(template, 'sms');
                }}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Select a template</option>
                {smsTemplates.map(template => (
                  <option key={template.id} value={template.id}>{template.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                SMS Message
              </label>
              <textarea
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                rows={4}
                maxLength={160}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Enter SMS message..."
              />
              <p className="text-xs text-text-secondary mt-1">
                {160 - smsMessage.length} characters remaining
              </p>
            </div>
          </div>
        );

      case 'export_data':
        return (
          <div className="space-y-4">
            <p className="text-text-secondary">
              Export client data for the selected clients in CSV format.
            </p>
            <div className="bg-secondary-50 p-4 rounded-lg">
              <h4 className="font-medium text-text-primary mb-2">Export will include:</h4>
              <ul className="text-sm text-text-secondary space-y-1">
                <li>• Client contact information</li>
                <li>• Case summary</li>
                <li>• Billing information</li>
                <li>• Communication history</li>
              </ul>
            </div>
          </div>
        );

      case 'update_status':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                New Status
              </label>
              <select className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Reason for Status Change
              </label>
              <textarea
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Enter reason for status change..."
              />
            </div>
          </div>
        );

      case 'assign_tag':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Select Tags
              </label>
              <div className="space-y-2">
                {['VIP Client', 'Payment Issues', 'Urgent Cases', 'Referral Source', 'Corporate Client'].map(tag => (
                  <label key={tag} className="flex items-center">
                    <input
                      type="checkbox"
                      className="mr-2 text-primary focus:ring-primary rounded"
                    />
                    <span className="text-text-primary">{tag}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-8">
            <Icon name="Settings" size={48} className="mx-auto text-text-secondary mb-4" />
            <p className="text-text-secondary">Select an action to perform on the selected clients.</p>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-surface rounded-lg shadow-dialog w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-heading font-semibold text-text-primary">
            Bulk Actions ({selectedClients.length} clients)
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary-100 rounded-lg transition-smooth focus:outline-none focus:ring-2 focus:ring-secondary-400"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Selected Clients */}
        <div className="p-6 border-b border-border">
          <h3 className="text-sm font-medium text-text-primary mb-3">Selected Clients:</h3>
          <div className="flex flex-wrap gap-2">
            {selectedClientData.slice(0, 5).map(client => (
              <span key={client.id} className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-primary-100 text-primary-700">
                {client.name}
              </span>
            ))}
            {selectedClientData.length > 5 && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700">
                +{selectedClientData.length - 5} more
              </span>
            )}
          </div>
        </div>

        {/* Action Selection */}
        <div className="p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-text-primary mb-3">
              Select Action
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => handleActionChange('send_email')}
                className={`flex items-center gap-3 p-4 border rounded-lg transition-smooth ${
                  selectedAction === 'send_email' ?'border-primary bg-primary-50 text-primary' :'border-border hover:border-secondary-300 text-text-primary'
                }`}
              >
                <Icon name="Mail" size={20} />
                <div className="text-left">
                  <div className="font-medium">Send Email</div>
                  <div className="text-xs opacity-70">Bulk email communication</div>
                </div>
              </button>

              <button
                onClick={() => handleActionChange('send_sms')}
                className={`flex items-center gap-3 p-4 border rounded-lg transition-smooth ${
                  selectedAction === 'send_sms' ?'border-primary bg-primary-50 text-primary' :'border-border hover:border-secondary-300 text-text-primary'
                }`}
              >
                <Icon name="MessageSquare" size={20} />
                <div className="text-left">
                  <div className="font-medium">Send SMS</div>
                  <div className="text-xs opacity-70">Bulk SMS messaging</div>
                </div>
              </button>

              <button
                onClick={() => handleActionChange('export_data')}
                className={`flex items-center gap-3 p-4 border rounded-lg transition-smooth ${
                  selectedAction === 'export_data'
                    ? 'border-primary bg-primary-50 text-primary' :'border-border hover:border-secondary-300 text-text-primary'
                }`}
              >
                <Icon name="Download" size={20} />
                <div className="text-left">
                  <div className="font-medium">Export Data</div>
                  <div className="text-xs opacity-70">Download client data</div>
                </div>
              </button>

              <button
                onClick={() => handleActionChange('update_status')}
                className={`flex items-center gap-3 p-4 border rounded-lg transition-smooth ${
                  selectedAction === 'update_status' ?'border-primary bg-primary-50 text-primary' :'border-border hover:border-secondary-300 text-text-primary'
                }`}
              >
                <Icon name="RefreshCw" size={20} />
                <div className="text-left">
                  <div className="font-medium">Update Status</div>
                  <div className="text-xs opacity-70">Change client status</div>
                </div>
              </button>

              <button
                onClick={() => handleActionChange('assign_tag')}
                className={`flex items-center gap-3 p-4 border rounded-lg transition-smooth ${
                  selectedAction === 'assign_tag' ?'border-primary bg-primary-50 text-primary' :'border-border hover:border-secondary-300 text-text-primary'
                }`}
              >
                <Icon name="Tag" size={20} />
                <div className="text-left">
                  <div className="font-medium">Assign Tags</div>
                  <div className="text-xs opacity-70">Add client tags</div>
                </div>
              </button>
            </div>
          </div>

          {/* Action Content */}
          <div className="mb-6">
            {renderActionContent()}
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <button
              onClick={onClose}
              className="px-4 py-2 text-text-secondary hover:text-text-primary hover:bg-secondary-100 rounded-lg transition-smooth focus:outline-none focus:ring-2 focus:ring-secondary-400"
            >
              Cancel
            </button>
            <button
              onClick={handleExecuteAction}
              disabled={!selectedAction || isProcessing}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-smooth focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 flex items-center gap-2"
            >
              {isProcessing && <Icon name="Loader2" size={16} className="animate-spin" />}
              {isProcessing ? 'Processing...' : 'Execute Action'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkActionsModal;